<template>
    <h2 class="mb-3 h2-small">
        <Translate translationKey="cryptocurrencies.converterTitle"
                   :fallback="`${cryptoCurrency.currency_code} to ${selectedCurrency.currency_code} Converter`"
                   :replacements="{
                       currency_code1: cryptoCurrency.currency_code,
                       currency_code2: selectedCurrency.currency_code,
                   }"/>
    </h2>
    <div class="converter">
        <div class="converter__header">
            <a :href="exchangeLink"
               target="_blank"
               rel="noopener noindex noreferrer">
                <img loading="lazy"
                     class="converter__logo"
                     :alt="exchangeName"
                     :title="exchangeName"
                     width="100%"
                     height="100%"
                     :src="exchangeLogo">
            </a>
            <div class="converter__score">
                <Translate translation-key="general.ourScore"
                           fallback="Our Score" />
            </div>
            <div class="converter__stars">
                <MenuStars :score="Number(exchangeScore)"
                           :starsCount="STAR_COUNT" />
                {{ exchangeScore }}
            </div>
        </div>
        <div class="converter__content"
             :class="{ 'converter__content--swapped': isSwapped }">
            <div class="converter__side">
                <ConverterOption v-model="selectedCryptocurrencyValue"
                                 showOnlyCrypto
                                 :inputWidth="inputWidth"
                                 :ticker="selectedCryptoCurrency.currency_code"
                                 :title="selectedCryptoCurrency.name"
                                 :logo="asset_cdn(selectedCryptoCurrency.logo, 32)"
                                 @input="updateSecondCurrencyValue"/>
            </div>
            <div class="converter__side">
                <ConverterOption v-model="selectedCurrencyValue"
                                 :ticker="selectedCurrency.currency_code"
                                 :title="selectedCurrency.name"
                                 :logo="asset_cdn(selectedCurrency.logo, 32)"
                                 emitEvent
                                 @inputRef="(event) => currencyInputRef = event"
                                 @input="updateCryptoValue"/>
            </div>
            <button class="converter__switch"
                    :aria-label="getTrans('cryptocurrencies.swapCurrencies', 'Swap Currencies')"
                    @click="swapCurrencies">
                <i class="fa-solid fa-right-left" />
            </button>
        </div>
        <a :href="exchangeLink"
           class="converter__button-container"
           target="_blank"
           rel="nofollow noopener noindex"
           data-category="converter-buy-button">
            <button class="converter__button btn btn-buy--big w-100">
                <Translate translationKey="cryptocurrencies.buyButton"
                           :fallback="`Buy ${selectedCryptoCurrency.name} Now`"
                           :replacements="{ currency_code: selectedCryptoCurrency.name }"/>
                <i class="fa-solid fa-arrow-right" />
            </button>
        </a>
    </div>
    <CurrencySelectModal v-if="converterModalStore.modals.showConverterCurrencyModal"
                         :isOpen="converterModalStore.modals.showConverterCurrencyModal"
                         :fiatCurrencies="props.fiatCurrencies"
                         :currentFiatCurrency="selectedCurrency"
                         :changeCurrencyProp="changeCurrency"
                         :changeCryptoCurrencyProp="changeCryptoCurrency"/>
</template>

<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    import { useEventListener } from '@vueuse/core';
    import ConverterOption from '@/js/cryptocurrencies/converter/ConverterOption.vue';
    import { asset_cdn } from '@/js/helpers/helpers';
    import { FiatCurrency } from '@/js/cryptocurrencies/types/fiatCurrency';
    import { CryptoCurrencyType } from '@/js/cryptocurrencies/types/cryptocurrencyType';
    import Translate from '@/js/helpers/Translate.vue';
    import MenuStars from '@/js/global/menu/components/MenuStars.vue';
    import { getTrans } from '@/js/helpers/getTrans';
    import CurrencySelectModal from '@/js/cryptocurrencies/converter/CurrencySelectModal.vue';
    import { useConverterModalStore } from '@/js/store/currency-converter/ConverterModalStore';
    import { formatNumber } from '@/js/cryptocurrencies/helpers/formatNumber';

    const props = defineProps<{
        cryptoCurrency: CryptoCurrencyType,
        fiatCurrency: FiatCurrency,
        fiatCurrencies: FiatCurrency[],
        exchangeName: string,
        exchangeLogo: string,
        exchangeScore: string,
        exchangeLink: string | null,
    }>();

    const converterModalStore = useConverterModalStore();

    const STAR_COUNT = 5;

    const currencyInputRef = ref(null);

    const inputWidth = ref('');
    const selectedCryptocurrencyValue = ref<string>('1');
    const selectedCurrencyValue = ref<string>('0');
    const selectedCryptoCurrency = ref(props.cryptoCurrency);
    const selectedCurrency = ref(props.fiatCurrency);

    const setInputWidth = () => {
        const inputRect = currencyInputRef.value.getBoundingClientRect();
        inputWidth.value = `${inputRect.width}px` || 'auto';
    };

    const isFiatCurrency = (currency: FiatCurrency | CryptoCurrencyType): boolean => 'sign' in currency;

    const updateSecondCurrencyValue = () => {
        const cryptoValue = Number(selectedCryptocurrencyValue.value);
        const cryptoPrice = Number(selectedCryptoCurrency.value.price);
        const secondaryPrice = Number(selectedCurrency.value.price);
        const newValue = cryptoValue * (cryptoPrice / secondaryPrice);
        const isFiat = isFiatCurrency(selectedCurrency.value);

        selectedCryptocurrencyValue.value = formatNumber(selectedCryptocurrencyValue.value, 0, 5);
        selectedCurrencyValue.value = formatNumber(newValue, 0, isFiat ? 2 : 5);
    };

    const updateCryptoValue = () => {
        const secondaryValue = Number(selectedCurrencyValue.value);
        const cryptoPrice = Number(selectedCryptoCurrency.value.price);
        const secondaryPrice = Number(selectedCurrency.value.price);
        const newValue = secondaryValue * (secondaryPrice / cryptoPrice);
        const isFiat = isFiatCurrency(selectedCurrency.value);

        selectedCurrencyValue.value = formatNumber(selectedCurrencyValue.value, 0, isFiat ? 2 : 5);
        selectedCryptocurrencyValue.value = formatNumber(newValue, 0, 5);
    };

    const changeCurrency = (currency: FiatCurrency | CryptoCurrencyType) => {
        selectedCurrency.value = currency;
        selectedCryptocurrencyValue.value = '1';
        selectedCurrencyValue.value = '0';
        updateSecondCurrencyValue();

        setTimeout(() => {
            setInputWidth();
        });
    };

    const changeCryptoCurrency = (cryptoCurrency: CryptoCurrencyType) => {
        selectedCryptoCurrency.value = cryptoCurrency;
        selectedCryptocurrencyValue.value = '1';
        selectedCurrencyValue.value = '0';
        updateSecondCurrencyValue();

        setTimeout(() => {
            setInputWidth();
        });
    };

    const isSwapped = ref(false);

    const swapCurrencies = () => {
        isSwapped.value = !isSwapped.value;
    };

    useEventListener(window, 'resize', setInputWidth);

    onMounted(() => {
        setInputWidth();
        updateSecondCurrencyValue();
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/mixins" as mixins;
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/design-system/variables" as variables;

.converter {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 12px;
    border: 4px solid legacyVariables.$up-color;
    border-radius: 16px;
    background-color: legacyVariables.$light;

    .btn-buy--big {
        @include mixins.btn-buy(10px 27px, 16px);
    }

    &__header {
        z-index: ZIndex.$converter-header-z-index;
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        padding-top: 12px;
        margin-bottom: -12px;
        background-color: variables.$baseWhite;
        border-top-left-radius: 12px;
        border-top-right-radius: 12px;
    }

    &__logo {
        width: 180px;
    }

    &__score {
        color: variables.$baseBlack;
        font-weight: 600;
    }

    &__stars {
        display: flex;
        align-items: center;
        justify-content: center;
        color: variables.$baseBlack;
        font-size: 24px;
        font-weight: 600;
        gap: 4px;

        :deep(.stars-container) {
            font-size: 18px;
        }
    }

    &__content {
        position: relative;
        display: flex;
        width: 100%;
        flex-direction: column;

        &--swapped {
            flex-direction: column-reverse;

            .converter__side {
                &:nth-child(1) {
                    background-color: legacyVariables.$light;
                    border-top-left-radius: 0;
                    border-top-right-radius: 0;
                }

                &:nth-child(2) {
                    background-color: variables.$baseWhite;
                    border-top-left-radius: 12px;
                    border-top-right-radius: 12px;
                }
            }
        }
    }

    &__side {
        width: 100%;
        height: 50%;

        &:nth-child(1) {
            background-color: variables.$baseWhite;
            border-top-left-radius: 12px;
            border-top-right-radius: 12px;
        }
    }

    &__button-container {
        width: 100%;
        padding-inline: 16px;
    }

    &__button {
        min-height: 46px;
    }

    &__switch {
        @include mixins.position-absolute-center;

        display: flex;
        width: min-content;
        align-items: center;
        justify-content: center;
        padding: 9px;
        border: 1px solid legacyVariables.$medium-grey;
        border-radius: 5px;
        aspect-ratio: 1;
        background: variables.$baseWhite;
        cursor: pointer;
        transition: opacity 150ms ease-in-out;

        &:hover {
            opacity: 0.8;
        }

        i {
            color: legacyVariables.$body-color;
            font-size: 12px;
            transform: rotate(90deg);
        }
    }
}

.dark-theme {
    .converter {
        background-color: legacyVariables.$royal-midnight;

        &__header {
            background-color: legacyVariables.$dark-theme-medium-purple;
        }

        &__logo {
            @include mixins.white-image;
        }

        &__score,
        &__stars {
            color: variables.$baseWhite;
        }

        &__content {
            &--swapped {
                .converter__side {
                    &:nth-child(1) {
                        background-color: legacyVariables.$royal-midnight;

                        :deep(.converter-option__input) {
                            background-color: legacyVariables.$dark-theme-medium-purple;
                        }
                    }

                    &:nth-child(2) {
                        background-color: legacyVariables.$dark-theme-medium-purple;

                        :deep(.converter-option__input) {
                            border: 1px solid legacyVariables.$purple;
                            background-color: legacyVariables.$dark-theme-bg;
                        }
                    }
                }
            }
        }

        &__side {
            &:nth-child(1) {
                background-color: legacyVariables.$dark-theme-medium-purple;

                :deep(.converter-option__input) {
                    border: 1px solid legacyVariables.$dark-theme-border;
                    background-color: legacyVariables.$dark-theme-bg;
                }
            }

            &:nth-child(2) {
                :deep(.converter-option__input) {
                    border: 1px solid legacyVariables.$purple;
                    background-color: legacyVariables.$dark-theme-medium-purple;
                }
            }
        }

        &__switch {
            border: 1px solid legacyVariables.$purple;
            background-color: legacyVariables.$purple;

            i {
                color: variables.$baseWhite;
                font-size: 12px;
            }
        }
    }
}

//Halving Page
.halving-page {
    .converter {
        &__switch {
            i {
                transform: rotate(0deg);
            }
        }

        @media (min-width: breakpoints.$lg-breakpoint) {
            &__header {
                padding-bottom: 12px;
            }

            &__content {
                flex-direction: row;

                &--swapped {
                    flex-direction: row-reverse;
                }

                .converter-option {
                    padding: 24px;
                }
            }

            &__side {
                width: 50%;
            }

            &__button-container {
                max-width: 850px;
                height: 46px;
                margin-top: 16px;
            }

            &__button {
                height: 100%;
                font-size: 16px;
            }
        }
    }
}
</style>
